/**
 * AdminAIChatWidget — chat AI *agentica* globale ARCHI-HUB (admin only).
 *
 * Rispetto alla versione precedente, l'AI ora esegue *function calling*:
 * - Chiama tool READ per interrogare fornitori, utenti, progetti, prodotti,
 *   log attività, richieste sconto, contatti RE, task IMTask (isolati per admin).
 * - Propone azioni WRITE (create_calendar_event, approve_user, reject_user,
 *   create_imtask_task) come card di conferma: l'admin deve premere
 *   "Conferma" o "Annulla" prima che vengano eseguite.
 *
 * SSE events gestiti:
 *  - {delta: "..."}                     → chunk di testo
 *  - {tool_running: {name,label,args}}  → tool READ in esecuzione
 *  - {tool_done: {name, confirmed?}}    → tool completato
 *  - {pending_action: {action_id,tool_name,tool_args,preview}}
 *                                       → mostra card di conferma
 *  - {done: true}                       → chiude stream
 *  - {error: "..."}                     → mostra errore
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, X, Trash2, Send, Loader2, MessageSquare, Wrench, CheckCircle2, XCircle, Mic, Square, Volume2, VolumeX, Store } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API } from "../lib/api";
import { useAdminNotificationEvents } from "../context/AdminNotificationsContext";

const MASCOT_LETTERS = ["G", "A", "S"];

// Pulisce il testo per la lettura vocale: toglie markdown, link ed emoji così
// l'assistente legge solo il contenuto (utile in auto, senza guardare lo schermo).
function stripForSpeech(text) {
  if (!text) return "";
  return text
    .replace(/\[([^\]]+)\]\s*\(https?:\/\/[^)\s]+\)/g, "$1")
    .replace(/https?:\/\/[^\s)\]]+/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/[#*_`>~|]/g, " ")
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE0F}]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

function pickItalianVoice() {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  return voices.find((v) => /^it(-|_)/i.test(v.lang)) || voices.find((v) => /it/i.test(v.lang)) || null;
}

// Dettatura vocale (Whisper): sceglie un formato audio supportato dal browser.
function pickMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const c of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

const SUGGESTED = [
  "Dammi email e condizioni commerciali di Flexform",
  "Quanti utenti pending? Elencali",
  "Quali sono i miei task IMTask scaduti?",
  "Elenca gli ultimi 5 progetti inviati",
];

export default function AdminAIChatWidget({ user }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [current, setCurrent] = useState("");
  const [runningTool, setRunningTool] = useState(null); // {name, label}
  const runningToolShownAtRef = useRef(0);
  const runningToolHideTimerRef = useRef(null);
  const setRunningToolWithMinDisplay = useCallback((next) => {
    if (next) {
      // show and record time
      if (runningToolHideTimerRef.current) {
        clearTimeout(runningToolHideTimerRef.current);
        runningToolHideTimerRef.current = null;
      }
      runningToolShownAtRef.current = Date.now();
      setRunningTool(next);
      return;
    }
    // hide with a min display of 500ms so users can see the tool label
    const elapsed = Date.now() - runningToolShownAtRef.current;
    const wait = Math.max(0, 500 - elapsed);
    if (runningToolHideTimerRef.current) clearTimeout(runningToolHideTimerRef.current);
    runningToolHideTimerRef.current = setTimeout(() => {
      setRunningTool(null);
      runningToolHideTimerRef.current = null;
    }, wait);
  }, []);
  const [pendingAction, setPendingAction] = useState(null); // {action_id, tool_name, tool_args, preview}
  const [cookieVisible, setCookieVisible] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const openRef = useRef(open);

  // Dettatura vocale (utile da mobile / in auto, a mani libere)
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const voiceSupported = typeof navigator !== "undefined" && !!navigator.mediaDevices && typeof MediaRecorder !== "undefined";

  // Registrazione "nuova visita" Store Insight: registra una nota vocale cliente e,
  // appena pronta, porta l'admin su Store Insight con la scheda di verifica aperta.
  const [siRecording, setSiRecording] = useState(false);
  const [siUploading, setSiUploading] = useState(false);
  const [siSecs, setSiSecs] = useState(0);
  const siRecorderRef = useRef(null);
  const siChunksRef = useRef([]);
  const siTimerRef = useRef(null);

  const uploadSiNote = async (blob, type) => {
    setSiUploading(true);
    try {
      const ext = type.includes("mp4") ? "mp4" : type.includes("webm") ? "webm" : "m4a";
      const fd = new FormData();
      fd.append("audio", blob, `visita.${ext}`);
      const token = localStorage.getItem("ah_token");
      const r = await fetch(`${API}/admin/store-insight/record`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const rawBody = await r.text();
      let data = {};
      try { data = rawBody ? JSON.parse(rawBody) : {}; } catch { data = {}; }
      if (!r.ok) throw new Error(data?.detail || "Non sono riuscito a registrare la visita, riprova.");
      // Passa il risultato a Store Insight (persistente + evento) e naviga lì.
      try { sessionStorage.setItem("si_pending_recording", JSON.stringify(data)); } catch { /* no-op */ }
      window.dispatchEvent(new CustomEvent("si:open-recording", { detail: data }));
      setOpen(false);
      navigate("/admin?tab=store-insight");
    } catch (err) {
      setMessages((prev) => [...prev, { role: "assistant", text: `🎙️ ${err?.message || "Errore registrazione visita."}` }]);
    } finally {
      setSiUploading(false);
    }
  };

  const startSiRecording = async () => {
    if (siRecording || recording || streaming || siUploading || transcribing || pendingAction) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      siChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) siChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(siChunksRef.current, { type });
        if (blob.size < 1500) return;
        await uploadSiNote(blob, type);
      };
      siRecorderRef.current = rec;
      rec.start();
      setSiRecording(true);
      setSiSecs(0);
      siTimerRef.current = setInterval(() => {
        setSiSecs((s) => { if (s >= 119) stopSiRecording(); return s + 1; });
      }, 1000);
    } catch {
      window.alert("Microfono non disponibile: verifica i permessi del browser per questo sito.");
    }
  };

  const stopSiRecording = () => {
    if (siTimerRef.current) { clearInterval(siTimerRef.current); siTimerRef.current = null; }
    setSiRecording(false);
    try { siRecorderRef.current?.stop(); } catch { /* no-op */ }
  };

  // Lettura ad alta voce delle risposte (TTS del browser, voce italiana)
  const speechSupported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [readAloud, setReadAloud] = useState(() => {
    try { return localStorage.getItem("ah_ai_read_aloud") === "1"; } catch { return false; }
  });
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const readAloudRef = useRef(readAloud);
  useEffect(() => { readAloudRef.current = readAloud; }, [readAloud]);

  const sessionId = "default";

  // ---- Nuovo messaggio dal team (Todo Team) → mascotte con la busta ----
  // Quando arriva un messaggio (nuovo compito / risposta / completamento) da
  // un altro admin, la mascotte cambia in quella con la busta e la lettera
  // del mittente, saltella e il fumetto dice chi ha scritto. Al click si apre
  // la chat AI che mostra il messaggio e chiede se vuoi rispondere.
  const [teamMsg, setTeamMsg] = useState(null); // {name, letter, taskId, kind}
  const isAdmin = !!user && user.role === "admin";
  const myId = user?.id;

  const fetchTeamMsg = useCallback(async () => {
    if (!isAdmin || !myId) return;
    try {
      const token = localStorage.getItem("ah_token");
      const r = await fetch(`${API}/admin/assigned-tasks`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) return;
      const tasks = await r.json();
      const ts = (s) => { const n = Date.parse(s || ""); return Number.isNaN(n) ? 0 : n; };
      const events = [];
      for (const t of tasks) {
        // "Visto" per-task lato server: il pulsante DA VEDERE/VISTO sul Todo
        // Team aggiorna viewed_by[myId]; qui contiamo solo gli eventi più
        // recenti dell'ultimo "visto".
        const seenTs = ts((t.viewed_by || {})[myId]);
        if (t.assignee_id === myId && t.creator_id !== myId && ts(t.created_at) > seenTs) {
          events.push({ t: ts(t.created_at), name: (t.creator_name || "").trim(), taskId: t.id, kind: "nuovo compito" });
        }
        if (t.creator_id === myId && t.assignee_id !== myId && t.status === "done" && ts(t.completed_at) > seenTs) {
          events.push({ t: ts(t.completed_at), name: (t.assignee_name || "").trim(), taskId: t.id, kind: "compito completato" });
        }
        for (const c of t.comments || []) {
          if (c.author_id !== myId && ts(c.created_at) > seenTs) {
            events.push({ t: ts(c.created_at), name: (c.author_name || "").trim(), taskId: t.id, kind: "risposta a un compito" });
          }
        }
      }
      if (!events.length) { setTeamMsg(null); return; }
      events.sort((a, b) => b.t - a.t);
      const latest = events[0];
      const letter = (latest.name || "?").charAt(0).toUpperCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const names = [...new Set(events.map((e) => e.name.split(" ")[0] || e.name))];
      setTeamMsg({
        name: latest.name,
        letter,
        taskId: latest.taskId,
        kind: latest.kind,
        count: events.length,
        names,
        events: events.slice(0, 5),
      });
    } catch { /* silente */ }
  }, [isAdmin, myId]);

  useEffect(() => { fetchTeamMsg(); }, [fetchTeamMsg]);
  useAdminNotificationEvents("assigned_task.", fetchTeamMsg);

  const joinNames = (names) =>
    names.length <= 1 ? names[0] : `${names.slice(0, -1).join(", ")} e ${names[names.length - 1]}`;

  const teamMsgBubbleText = teamMsg
    ? teamMsg.count > 1
      ? `${teamMsg.count} nuovi messaggi da ${joinNames(teamMsg.names)}`
      : `${teamMsg.name.split(" ")[0] || teamMsg.name} ha un nuovo messaggio per te`
    : "";

  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    const check = () => {
      try {
        setCookieVisible(!localStorage.getItem("ah_cookie_consent_v1"));
      } catch {
        setCookieVisible(false);
      }
    };
    check();
    const iv = setInterval(check, 2000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!open || typeof Notification === "undefined") return undefined;
    if (Notification.permission === "default") {
      const alreadyPrompted = localStorage.getItem("ah_notif_prompted");
      if (alreadyPrompted) {
        setNotifPermission(Notification.permission);
        return undefined;
      }
      const t = setTimeout(() => {
        localStorage.setItem("ah_notif_prompted", "1");
        Notification.requestPermission()
          .then((p) => setNotifPermission(p))
          .catch(() => {});
      }, 1500);
      return () => clearTimeout(t);
    }
    setNotifPermission(Notification.permission);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (open && hasUnread) setHasUnread(false);
  }, [open, hasUnread]);

  const showBrowserNotification = (fullText) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    const preview = (fullText || "")
      .replace(/[*_`#]/g, "")
      .replace(/\n+/g, " ")
      .trim()
      .slice(0, 140);
    try {
      const n = new Notification("Assistente AI ARCHI-HUB", {
        body: preview || "Risposta pronta.",
        tag: "archihub-ai-reply",
        renotify: false,
      });
      n.onclick = () => {
        window.focus();
        setOpen(true);
        n.close();
      };
    } catch {
      // ignore
    }
  };

  const loadHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem("ah_token");
      const r = await fetch(
        `${API}/admin/ai/chat/history?session_id=${sessionId}`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (r.ok) {
        const hist = await r.json();
        setMessages(hist.map((m) => ({ role: m.role, text: m.text })));
      }
    } catch {
      // silente
    }
  }, []);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  useEffect(() => {
    if (open) {
      const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, current, runningTool, pendingAction]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  /**
   * Consuma uno stream SSE e aggiorna gli stati:
   * - accumula testo delta in localAcc → onDelta callback
   * - traccia tool in esecuzione via runningTool
   * - alla ricezione di pending_action, imposta pendingAction (che ferma il ciclo)
   * - al done, restituisce l'accumulato per essere inserito come messaggio finale.
   */
  const consumeStream = async (resp) => {
    if (!resp.ok || !resp.body) {
      throw new Error(`Errore chat (${resp.status})`);
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let acc = "";
    let sawPendingAction = false;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        let json;
        try { json = JSON.parse(line.slice(6)); } catch { continue; }
        if (json.delta) {
          acc += json.delta;
          setCurrent(acc);
        }
        if (json.tool_running) {
          setRunningToolWithMinDisplay({
            name: json.tool_running.name,
            label: json.tool_running.label || `sto usando ${json.tool_running.name}`,
          });
        }
        if (json.tool_done) {
          setRunningToolWithMinDisplay(null);
          // Notifica le pagine aperte (es. IMTask) che l'AI ha modificato dei dati
          try {
            window.dispatchEvent(new CustomEvent("ai-tool-done", { detail: { name: json.tool_done.name } }));
          } catch { /* no-op */ }
        }
        if (json.pending_action) {
          sawPendingAction = true;
          setPendingAction(json.pending_action);
        }
        if (json.error) {
          throw new Error(json.error);
        }
        if (json.done) {
          // stop reading after done marker
        }
      }
    }
    return { text: acc, sawPendingAction };
  };

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: message }]);
    setStreaming(true);
    setCurrent("");
    setRunningTool(null);
    setPendingAction(null);
    try {
      const token = localStorage.getItem("ah_token");
      const resp = await fetch(`${API}/admin/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message, session_id: sessionId }),
      });
      const { text: acc, sawPendingAction } = await consumeStream(resp);
      if (acc) {
        setMessages((prev) => [...prev, { role: "assistant", text: acc }]);
        speakIfEnabled(acc);
        if (!openRef.current) {
          setHasUnread(true);
          showBrowserNotification(acc);
        }
      } else if (sawPendingAction && !openRef.current) {
        setHasUnread(true);
        showBrowserNotification("Ho un'azione da confermare");
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: `Errore: ${err?.message || "impossibile contattare l'AI"}` },
      ]);
      if (!openRef.current) setHasUnread(true);
    } finally {
      setStreaming(false);
      setCurrent("");
      setRunningTool(null);
    }
  };

  // ---------- Dettatura vocale ----------
  const startRecording = async () => {
    if (recording || streaming || transcribing || pendingAction) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 1500) return; // registrazione troppo corta
        await transcribeAudio(blob, type);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setRecSecs(0);
      recTimerRef.current = setInterval(() => {
        setRecSecs((s) => {
          if (s >= 89) stopRecording();
          return s + 1;
        });
      }, 1000);
    } catch {
      window.alert("Microfono non disponibile: verifica i permessi del browser per questo sito.");
    }
  };

  const stopRecording = () => {
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
    setRecording(false);
    try { recorderRef.current?.stop(); } catch { /* no-op */ }
  };

  const transcribeAudio = async (blob, type) => {
    setTranscribing(true);
    try {
      const ext = type.includes("mp4") ? "mp4" : type.includes("webm") ? "webm" : "m4a";
      const fd = new FormData();
      fd.append("audio", blob, `dettatura.${ext}`);
      const token = localStorage.getItem("ah_token");
      const r = await fetch(`${API}/admin/mobile/transcribe`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      // Risposta robusta: se non è JSON (es. pagina HTML di errore del proxy /
      // timeout del gateway) evitiamo l'errore "Unexpected token '<'".
      const rawBody = await r.text();
      let data = {};
      try { data = rawBody ? JSON.parse(rawBody) : {}; } catch { data = {}; }
      if (!r.ok) {
        throw new Error(data?.detail || "Servizio di trascrizione non raggiungibile in questo momento, riprova tra poco.");
      }
      const text = (data.text || "").trim();
      if (text) await send(text); // a mani libere: detta → trascrive → invia
      else throw new Error("Non ho capito l'audio, riprova parlando più vicino al microfono.");
    } catch (err) {
      setMessages((prev) => [...prev, { role: "assistant", text: `🎙️ ${err?.message || "Non ho capito l'audio, riprova."}` }]);
    } finally {
      setTranscribing(false);
    }
  };

  useEffect(() => () => { if (recTimerRef.current) clearInterval(recTimerRef.current); if (siTimerRef.current) clearInterval(siTimerRef.current); }, []);

  // ---------- Lettura ad alta voce (TTS) ----------
  const stopSpeaking = useCallback(() => {
    if (speechSupported) { try { window.speechSynthesis.cancel(); } catch { /* no-op */ } }
    setSpeakingIdx(null);
  }, [speechSupported]);

  const speak = useCallback((text, idx = -1) => {
    if (!speechSupported) return;
    const clean = stripForSpeech(text);
    if (!clean) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(clean);
      u.lang = "it-IT";
      const v = pickItalianVoice();
      if (v) u.voice = v;
      u.rate = 1;
      u.onend = () => setSpeakingIdx(null);
      u.onerror = () => setSpeakingIdx(null);
      setSpeakingIdx(idx);
      window.speechSynthesis.speak(u);
    } catch { setSpeakingIdx(null); }
  }, [speechSupported]);

  const speakIfEnabled = useCallback((text) => {
    if (readAloudRef.current) speak(text, -1);
  }, [speak]);

  const toggleReadAloud = () => {
    setReadAloud((on) => {
      const next = !on;
      try { localStorage.setItem("ah_ai_read_aloud", next ? "1" : "0"); } catch { /* no-op */ }
      if (!next) stopSpeaking();
      return next;
    });
  };

  // Ferma la voce quando il pannello si chiude o il componente viene smontato.
  useEffect(() => { if (!open) stopSpeaking(); }, [open, stopSpeaking]);
  useEffect(() => () => stopSpeaking(), [stopSpeaking]);

  // Pre-carica l'elenco voci del browser così la voce italiana è già pronta
  // alla prima risposta letta (su Chromium getVoices() è vuoto al primo accesso).
  useEffect(() => {
    if (!speechSupported) return;
    try { window.speechSynthesis.getVoices(); } catch { /* no-op */ }
    const handler = () => {};
    window.speechSynthesis.onvoiceschanged = handler;
    return () => { if (window.speechSynthesis.onvoiceschanged === handler) window.speechSynthesis.onvoiceschanged = null; };
  }, [speechSupported]);

  /**
   * L'admin ha confermato/annullato l'azione WRITE proposta dall'AI.
   * Chiamiamo di nuovo /admin/ai/chat con resume_action_id.
   */
  const resolveAction = async (confirmed) => {
    if (!pendingAction || streaming) return;
    const action = pendingAction;
    setPendingAction(null);
    setStreaming(true);
    setCurrent("");
    setRunningTool(null);
    // Aggiungi bubble di conferma nel log
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        text: confirmed
          ? `✓ Confermato: ${action.preview?.title || action.tool_name}`
          : `✗ Annullato: ${action.preview?.title || action.tool_name}`,
      },
    ]);
    try {
      const token = localStorage.getItem("ah_token");
      const resp = await fetch(`${API}/admin/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          resume_action_id: action.action_id,
          confirmed,
          session_id: sessionId,
        }),
      });
      const { text: acc } = await consumeStream(resp);
      if (acc) {
        setMessages((prev) => [...prev, { role: "assistant", text: acc }]);
        speakIfEnabled(acc);
        if (!openRef.current) {
          setHasUnread(true);
          showBrowserNotification(acc);
        }
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: `Errore: ${err?.message || "impossibile completare l'azione"}` },
      ]);
    } finally {
      setStreaming(false);
      setCurrent("");
      setRunningTool(null);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm("Cancellare tutta la cronologia della chat?")) return;
    try {
      const token = localStorage.getItem("ah_token");
      await fetch(`${API}/admin/ai/chat/history?session_id=${sessionId}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMessages([]);
      setPendingAction(null);
    } catch {
      // silente
    }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <>
      {!open && (
        <>
          {/* Animazione "saluto" della mascotte AI + fumetto */}
          <style>{`
            @keyframes ai-mascot-wave {
              0%, 55%, 100% { transform: rotate(0deg); }
              63% { transform: rotate(-14deg); }
              71% { transform: rotate(10deg); }
              79% { transform: rotate(-8deg); }
              87% { transform: rotate(4deg); }
            }
            @keyframes ai-mascot-idle {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-3px); }
            }
            @keyframes ai-bubble-pop {
              0% { opacity: 0; transform: translateY(6px) scale(0.9); }
              12% { opacity: 1; transform: translateY(0) scale(1); }
              88% { opacity: 1; transform: translateY(0) scale(1); }
              100% { opacity: 0; transform: translateY(6px) scale(0.95); }
            }
            .ai-mascot-btn .ai-mascot-float { animation: ai-mascot-idle 3.2s ease-in-out infinite; display: block; }
            .ai-mascot-btn:hover .ai-mascot-float { animation: none; }
            .ai-mascot-img {
              transform-origin: 50% 90%;
              animation: ai-mascot-wave 5s ease-in-out infinite;
            }
            .ai-mascot-btn:hover .ai-mascot-img { animation-duration: 1.6s; }
            .ai-mascot-bubble {
              animation: ai-bubble-pop 6s ease-in-out infinite;
              animation-delay: 1.2s;
            }
            @keyframes ai-mascot-jump {
              0%, 100% { transform: translateY(0) rotate(-3deg); }
              25% { transform: translateY(-7px) rotate(3deg); }
              50% { transform: translateY(0) rotate(-2deg); }
              75% { transform: translateY(-4px) rotate(2deg); }
            }
            .ai-mascot-img-msg {
              transform-origin: 50% 90%;
              animation: ai-mascot-jump 0.9s ease-in-out infinite;
            }
            @keyframes ai-bubble-in {
              0% { opacity: 0; transform: translateY(6px) scale(0.9); }
              100% { opacity: 1; transform: translateY(0) scale(1); }
            }
            .ai-mascot-bubble-msg { animation: ai-bubble-in 0.5s ease-out both; }
            @media (prefers-reduced-motion: reduce) {
              .ai-mascot-float, .ai-mascot-img, .ai-mascot-img-msg, .ai-mascot-bubble, .ai-mascot-bubble-msg { animation: none !important; }
            }
          `}</style>
          <button
            type="button"
            onClick={() => setOpen(true)}
            style={{ bottom: cookieVisible ? "9rem" : "1.25rem" }}
            className="ai-mascot-btn fixed right-5 z-40 group flex items-center justify-center w-[72px] h-[72px] rounded-full bg-gradient-to-br from-white to-amber-50 hover:from-amber-50 hover:to-amber-100 shadow-2xl hover:shadow-gold/40 ring-2 ring-gold/40 hover:ring-gold transition-all hover:scale-110"
            data-testid="admin-ai-chat-toggle"
            data-team-msg={teamMsg ? teamMsg.letter : undefined}
            title={
              teamMsg
                ? `${teamMsgBubbleText} · marca "Visto" sul Todo Team`
                : hasUnread
                ? "Nuova risposta AI · clicca per leggere"
                : "Assistente AI ARCHI-HUB (clicca per aprire)"
            }
            aria-label={teamMsg ? "Nuovo messaggio dal team" : hasUnread ? "Assistente AI (nuova risposta)" : "Apri assistente AI"}
          >
            <span className="ai-mascot-float pointer-events-none">
              <img
                src={teamMsg && MASCOT_LETTERS.includes(teamMsg.letter) ? `/mascots/mascot_${teamMsg.letter}.png` : "/ai-mascot.png"}
                alt=""
                draggable={false}
                className={`${teamMsg ? "ai-mascot-img-msg" : "ai-mascot-img"} w-[68px] h-[68px] object-contain select-none pointer-events-none drop-shadow`}
              />
            </span>
            {/* Lettera mittente (solo se non esiste la mascotte con quella lettera) */}
            {teamMsg && !MASCOT_LETTERS.includes(teamMsg.letter) && (
              <span className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-gold text-ink text-xs font-bold flex items-center justify-center ring-2 ring-white pointer-events-none">
                {teamMsg.letter}
              </span>
            )}
            {/* Contatore messaggi multipli */}
            {teamMsg && teamMsg.count > 1 && (
              <span
                className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white pointer-events-none"
                data-testid="ai-team-msg-count"
              >
                {teamMsg.count}
              </span>
            )}
            {/* Fumetto */}
            {teamMsg ? (
              <span
                className="ai-mascot-bubble-msg absolute -top-9 right-0 bg-white text-ink text-[11px] font-bold tracking-wide px-2.5 py-1 rounded-xl rounded-br-none shadow-lg border border-gold/50 whitespace-nowrap pointer-events-none"
                data-testid="ai-team-msg-bubble"
              >
                📩 {teamMsgBubbleText}
              </span>
            ) : (
              <span
                className="ai-mascot-bubble absolute -top-8 -left-10 bg-white text-ink text-[11px] font-bold tracking-wide px-2.5 py-1 rounded-xl rounded-br-none shadow-lg border border-ink/10 whitespace-nowrap pointer-events-none"
                aria-hidden="true"
              >
                Ciao! <span className="text-gold">✦</span>
              </span>
            )}
            {hasUnread ? (
              <span
                className="absolute top-0 right-0 min-w-[20px] h-[20px] px-1 rounded-full bg-rose-500 ring-2 ring-white text-[10px] font-bold text-white flex items-center justify-center animate-bounce"
                data-testid="admin-ai-unread-badge"
              >
                ●
              </span>
            ) : streaming ? (
              <span
                className="absolute top-1 right-1 w-3 h-3 rounded-full bg-amber-400 ring-2 ring-white animate-pulse"
                title="AI sta rispondendo..."
                data-testid="admin-ai-streaming-badge"
              />
            ) : (
              <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
            )}
          </button>
        </>
      )}

      {open && (
        <>
          <button
            type="button"
            aria-label="Chiudi assistente"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-sm md:hidden"
            data-testid="admin-ai-chat-backdrop"
          />
          <div
            className="fixed bottom-5 right-5 z-50 w-[92vw] sm:w-[440px] h-[calc(100vh-6.5rem)] sm:h-[640px] max-h-[85vh] bg-white rounded-xl shadow-2xl border border-ink/10 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4"
            data-testid="admin-ai-chat-panel"
          >
            <div className="bg-gradient-to-br from-ink to-gold text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="bg-white/20 p-2 rounded-lg backdrop-blur">
                  <Sparkles size={18} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="font-display text-base leading-tight">AI Agent</h3>
                  <p className="text-[10px] text-amber-100/90 tracking-wide">ARCHI-HUB + IMTask · Gemini 3.1 Pro · con tool</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {speechSupported && (
                  <button
                    onClick={toggleReadAloud}
                    className={`p-1.5 rounded-lg transition ring-1 ${readAloud ? "bg-white/25 ring-white/50" : "hover:bg-white/15 ring-transparent"}`}
                    title={readAloud ? "Lettura ad alta voce attiva (clicca per disattivare)" : "Attiva la lettura ad alta voce delle risposte"}
                    data-testid="admin-ai-readaloud-toggle"
                    aria-label="Lettura ad alta voce"
                    aria-pressed={readAloud}
                  >
                    {readAloud ? <Volume2 size={15} /> : <VolumeX size={15} />}
                  </button>
                )}
                {messages.length > 0 && (
                  <button
                    onClick={clearHistory}
                    className="p-1.5 hover:bg-white/15 rounded-lg transition"
                    title="Cancella cronologia"
                    data-testid="admin-ai-chat-clear"
                    aria-label="Cancella cronologia"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 hover:bg-white/15 rounded-lg transition ring-1 ring-white/30"
                  title="Chiudi (ESC)"
                  data-testid="admin-ai-chat-close"
                  aria-label="Chiudi assistente"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-cream/30 space-y-3">
              {notifPermission === "default" && messages.length === 0 && !current && (
                <button
                  type="button"
                  onClick={() => {
                    localStorage.setItem("ah_notif_prompted", "1");
                    Notification.requestPermission()
                      .then((p) => setNotifPermission(p))
                      .catch(() => {});
                  }}
                  className="w-full text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 mb-1 hover:bg-amber-100 flex items-center justify-between"
                  data-testid="admin-ai-notif-permission"
                >
                  <span>Abilita notifiche desktop per ricevere le risposte anche a pannello chiuso</span>
                  <span className="font-semibold underline">Abilita</span>
                </button>
              )}
              {messages.length === 0 && !current && (
                <div className="text-center py-6">
                  <div className="inline-flex bg-gradient-to-br from-ink/10 to-gold/20 rounded-full p-4 mb-3">
                    <MessageSquare size={26} className="text-ink" strokeWidth={1.5} />
                  </div>
                  <p className="text-slate-600 text-sm mb-4 max-w-[280px] mx-auto">
                    Interrogami su fornitori, utenti, progetti, task IMTask. Posso anche eseguire azioni con la tua conferma.
                  </p>
                  <div className="space-y-2">
                    {SUGGESTED.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => send(s)}
                        disabled={streaming}
                        className="w-full text-left text-sm px-3 py-2 bg-white border border-slate-200 rounded-lg hover:bg-white hover:border-gold hover:shadow-sm transition"
                        data-testid={`admin-ai-suggested-${i}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <Bubble
                  key={i}
                  role={m.role}
                  text={m.text}
                  canSpeak={speechSupported && m.role === "assistant"}
                  speaking={speakingIdx === i}
                  onSpeak={() => (speakingIdx === i ? stopSpeaking() : speak(m.text, i))}
                />
              ))}

              {current && <Bubble role="assistant" text={current} streaming />}

              {runningTool && (
                <div
                  className="flex items-center gap-2 text-slate-600 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                  data-testid="admin-ai-tool-running"
                >
                  <Wrench size={14} className="text-amber-700 animate-pulse" />
                  <span className="italic">{runningTool.label}...</span>
                </div>
              )}

              {pendingAction && (
                <PendingActionCard
                  action={pendingAction}
                  streaming={streaming}
                  onConfirm={() => resolveAction(true)}
                  onCancel={() => resolveAction(false)}
                />
              )}

              {streaming && !current && !runningTool && !pendingAction && (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Loader2 size={14} className="animate-spin" /> Sto pensando...
                </div>
              )}
            </div>

            <div className="p-3 border-t border-ink/10 bg-white">
              {(recording || transcribing) && (
                <div className="mb-2 flex items-center gap-2 text-xs text-slate-600" data-testid="admin-ai-voice-status">
                  {recording ? (
                    <>
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
                      Registrazione in corso… {recSecs}s — premi stop per inviare
                    </>
                  ) : (
                    <><Loader2 size={13} className="animate-spin" /> Sto trascrivendo la dettatura…</>
                  )}
                </div>
              )}
              {(siRecording || siUploading) && (
                <div className="mb-2 flex items-center gap-2 text-xs text-ink font-medium" data-testid="admin-ai-si-status">
                  {siRecording ? (
                    <>
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
                      Registro la nuova visita… {siSecs}s — premi stop per verificare i dati
                    </>
                  ) : (
                    <><Loader2 size={13} className="animate-spin" /> Trascrivo e apro la scheda di verifica…</>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                {voiceSupported && (
                  <button
                    type="button"
                    onClick={recording ? stopRecording : startRecording}
                    disabled={streaming || !!pendingAction || transcribing || siRecording || siUploading}
                    className={`p-2.5 rounded-lg transition disabled:opacity-40 ${recording ? "bg-rose-500 hover:bg-rose-600 text-white animate-pulse" : "bg-gold/15 hover:bg-gold/25 text-ink"}`}
                    data-testid="admin-ai-voice-btn"
                    title={recording ? "Ferma e invia" : "Detta a voce (anche in auto)"}
                    aria-label={recording ? "Ferma registrazione" : "Dettatura vocale"}
                  >
                    {recording ? <Square size={16} /> : <Mic size={16} />}
                  </button>
                )}
                {voiceSupported && (
                  <button
                    type="button"
                    onClick={siRecording ? stopSiRecording : startSiRecording}
                    disabled={streaming || !!pendingAction || recording || transcribing || siUploading}
                    className={`p-2.5 rounded-lg transition disabled:opacity-40 ${siRecording ? "bg-rose-500 hover:bg-rose-600 text-white animate-pulse" : "bg-ink/5 hover:bg-ink/10 text-ink"}`}
                    data-testid="admin-ai-si-rec-btn"
                    title={siRecording ? "Ferma e apri la verifica dati" : "Registra una nuova visita (Store Insight)"}
                    aria-label={siRecording ? "Ferma registrazione visita" : "Registra nuova visita"}
                  >
                    {siUploading ? <Loader2 size={16} className="animate-spin" /> : siRecording ? <Square size={16} /> : <Store size={16} />}
                  </button>
                )}
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                  placeholder={streaming ? "Attendere..." : (pendingAction ? "In attesa di conferma..." : (recording ? "Sto ascoltando..." : "Chiedi qualcosa..."))}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold outline-none text-sm disabled:bg-slate-50"
                  disabled={streaming || !!pendingAction}
                  data-testid="admin-ai-chat-input"
                />
                <button
                  onClick={() => send()}
                  disabled={streaming || !!pendingAction || !input.trim()}
                  className="bg-ink hover:bg-slate-800 text-white p-2.5 rounded-lg disabled:opacity-40 transition"
                  data-testid="admin-ai-chat-send"
                  aria-label="Invia"
                >
                  {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
              <div className="mt-1.5 text-[9px] text-slate-400 text-center">
                L&apos;agente interroga i database in tempo reale. Le azioni richiedono la tua conferma.
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// -------- Confirmation card per azioni WRITE proposte dall'AI --------
function PendingActionCard({ action, streaming, onConfirm, onCancel }) {
  const preview = action.preview || {};
  return (
    <div
      className="border-2 border-gold bg-gradient-to-br from-amber-50 to-white rounded-lg p-3.5 shadow-md"
      data-testid="admin-ai-pending-action"
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="bg-gold/20 p-1.5 rounded-md">
          <Wrench size={14} className="text-amber-800" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.18em] uppercase text-amber-700 font-semibold">
            Conferma azione
          </div>
          <div className="text-sm font-semibold text-ink mt-0.5">
            {preview.title || action.tool_name}
          </div>
        </div>
      </div>

      {Array.isArray(preview.lines) && preview.lines.length > 0 && (
        <ul className="text-xs text-slate-700 space-y-1 mb-3 pl-1">
          {preview.lines.map((ln, i) => (
            <li key={i} className="break-words">
              <span className="text-slate-400">›</span> {ln}
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={streaming}
          className="flex-1 bg-ink hover:bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider py-2 rounded flex items-center justify-center gap-1.5 disabled:opacity-50 transition"
          data-testid="admin-ai-action-confirm"
        >
          <CheckCircle2 size={14} /> {preview.cta_confirm || "Conferma"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={streaming}
          className="flex-1 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs font-semibold uppercase tracking-wider py-2 rounded flex items-center justify-center gap-1.5 disabled:opacity-50 transition"
          data-testid="admin-ai-action-cancel"
        >
          <XCircle size={14} /> {preview.cta_cancel || "Annulla"}
        </button>
      </div>
    </div>
  );
}

// -------- Bubble --------
function Bubble({ role, text, streaming, canSpeak, speaking, onSpeak }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={isUser ? "admin-ai-user-msg" : "admin-ai-assistant-msg"}>
      <div
        className={`group relative max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${
          isUser
            ? "bg-ink text-white rounded-br-md"
            : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"
        }`}
      >
        {renderMd(text)}
        {streaming && !isUser && <span className="inline-block w-1.5 h-3.5 bg-ink/60 ml-1 animate-pulse align-middle" />}
        {canSpeak && !streaming && (
          <button
            type="button"
            onClick={onSpeak}
            className={`mt-1.5 inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase rounded px-1.5 py-0.5 transition ${speaking ? "bg-gold/20 text-ink" : "text-slate-400 hover:text-ink hover:bg-slate-100"}`}
            data-testid="admin-ai-speak-btn"
            title={speaking ? "Ferma lettura" : "Leggi ad alta voce"}
            aria-label={speaking ? "Ferma lettura" : "Leggi ad alta voce"}
          >
            {speaking ? <Square size={11} /> : <Volume2 size={11} />} {speaking ? "Stop" : "Ascolta"}
          </button>
        )}
      </div>
    </div>
  );
}

function renderMd(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\s*\(https?:\/\/[^)\s]+\)|https?:\/\/[^\s)\]]+)/g);
  return parts.map((p, i) => {
    if (!p) return null;
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    const m = p.match(/^\[([^\]]+)\]\s*\((https?:\/\/[^)\s]+)\)$/);
    if (m) {
      return (
        <a key={i} href={m[2]} target="_blank" rel="noreferrer" className="text-blue-700 underline hover:text-blue-900 font-medium">
          {m[1]}
        </a>
      );
    }
    if (/^https?:\/\//.test(p)) {
      return (
        <a key={i} href={p} target="_blank" rel="noreferrer" className="text-blue-700 underline hover:text-blue-900 font-medium break-all">
          {p.length > 50 ? `${p.slice(0, 47)}…` : p}
        </a>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}
